import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { DialogActions, DialogContent } from '@mui/material';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Typography from 'src/components/ui/Typography';
import Button from 'src/components/ui/Button';
import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import IconButton from 'src/components/ui/IconButton';
import TextField from 'src/components/ui/TextField';

import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';
import { styledComponent } from 'src/utils/styled';

const StyledDialog = styledComponent(Dialog)`
    width: fit-content;
    margin: auto;
    .MuiPaper-root {
        padding: 1rem;
    }
    @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
        width: 100vw;
        .MuiPaper-root {
            padding: 0;
            width: 100%;
            height: 100%;
            max-height: 100%;
            margin: 0;
            border-radius: 0;
            display: flex;
            justify-content: center;
            align-items: center;
        }
    }
`;

const StyledDialogActions = styledComponent(DialogActions)`
    padding: 0.5rem;
`;

const StyledDialogContent = styledComponent(DialogContent)`
    width: 22.75rem;
    padding: 0 0.625rem;
`;

const StyledButton = styledComponent(Button)`
    width: 6.25rem;
`;

const SkipText = styledComponent(Typography)`
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;

const CloseButton = styledComponent(IconButton)`
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 10;
    padding: 0.2rem;
    background-color: ${colours.white.main};
`;

interface Props {
    open: boolean;
    onSkip: () => void;
    onCloseOrCancel: () => void;
    onConfirm: (value: number) => void;
}

const SoldPropertyInfosModal: React.FC<Props> = ({ open, onSkip, onCloseOrCancel, onConfirm }) => {
    const [priceSold, setPriceSold] = useState<number | undefined>(undefined);
    const { t } = useTranslation(['common', 'edit-profile']);

    const onPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setPriceSold(newValue ? Number(newValue) : undefined);
    };

    const handleSubmit = () => {
        if (priceSold) {
            onConfirm(priceSold);
        }
    };

    return (
        <StyledDialog open={open} onClose={onCloseOrCancel}>
            <Box>
                <Formik
                    initialValues={{
                        soldPrice: '',
                    }}
                    onSubmit={handleSubmit}
                >
                    <Form>
                        <CloseButton
                            data-target="sold-ad-infos-button"
                            data-target-id="sold-ad-infos-close"
                            color="white"
                            onClick={onCloseOrCancel}
                            iconProps={{
                                name: 'Close',
                            }}
                        />
                        <DialogTitle>
                            <Typography
                                data-target="sold-ad-infos-text"
                                data-target-id="sold-ad-infos-title"
                                variant="h2"
                            >
                                {t('edit-profile:soldAdInfosModalTitle')}
                            </Typography>
                        </DialogTitle>
                        <StyledDialogContent>
                            <TextField
                                type="number"
                                name="soldPrice"
                                label={t('edit-profile:housePrice')}
                                onChange={onPriceChange}
                                InputProps={{
                                    inputProps: {
                                        'data-target': 'sold-ad-infos-text-field',
                                        'data-target-id': 'sold-ad-infos-price',
                                    },
                                    endAdornment: <Typography variant="subtitle1">€</Typography>,
                                }}
                                formatNumbers
                            />
                        </StyledDialogContent>
                        <StyledDialogActions>
                            <Box mx="1rem">
                                <SkipText onClick={onSkip}>{t(`common:modal.skip`)}</SkipText>
                            </Box>
                            <StyledButton
                                data-target="sold-ad-infos-button"
                                data-target-id="sold-ad-infos-cancel"
                                variant="outlined"
                                onClick={onCloseOrCancel}
                            >
                                {t(`common:modal.cancel`)}
                            </StyledButton>
                            <StyledButton
                                type="submit"
                                disabled={priceSold === undefined}
                                data-target="sold-ad-infos-button"
                                data-target-id="sold-ad-infos-confirm"
                            >
                                {t(`common:modal.confirm`)}
                            </StyledButton>
                        </StyledDialogActions>
                    </Form>
                </Formik>
            </Box>
        </StyledDialog>
    );
};

export default SoldPropertyInfosModal;
